<div class="container py-3">
  <div class="d-flex align-items-center mb-3">
    <h2 class="m-0">{{ title }}</h2>
    <button class="btn btn-link btn-sm p-0 text-primary ms-2"
            (click)="openHelp()"
            matTooltip="How to use RTK Base Analyzer">
      <span class="material-icons" aria-hidden="true">menu_book</span>
    </button>
  </div>

  <ng-container *ngIf="selectedConnection as conn; else emptyState">
    <div class="mp-card">
      <div class="row g-3">
        <div class="col-12 col-lg-7">
          <div class="subsection">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="d-flex align-items-center gap-2">
                <h4 class="subsection-title m-0">Analyze RTK Base</h4>
                <button class="btn btn-link btn-sm p-0 text-muted"
                        matTooltip="Run an analyzer task in backend to retrieve RTCM data from the selected mount point and generate a report.">
                  <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                </button>
              </div>
              <div class="text-muted small" *ngIf="analysisStatusByMountpoint[conn.mountPoint || '']">
                {{ analysisStatusByMountpoint[conn.mountPoint || ''] }}
              </div>
            </div>

            <div class="d-flex justify-content-end mb-2">
              <button class="btn btn-link btn-sm p-0 text-secondary text-decoration-none"
                      (click)="toggleQuickGuide()"
                      [attr.aria-expanded]="quickGuideExpanded">
                <span class="d-inline-flex align-items-center">
                  <span class="material-icons me-1 text-warning" style="font-size: 18px;" aria-hidden="true">menu_book</span>
                  <span class="me-1">Quick Guide</span>
                  <span class="material-icons" style="font-size: 20px;">
                    {{ quickGuideExpanded ? 'expand_less' : 'expand_more' }}
                  </span>
                </span>
              </button>
            </div>

            <!-- Quick Guide -->
            <div class="quick-start mb-3" *ngIf="quickGuideExpanded">
              <div class="quick-start-title mb-2">Steps:</div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">1</span>
                  <span class="step-text">Setup MountPoint Connection</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepSetupStatus.tone === 'success', 'bg-secondary': stepSetupStatus.tone === 'muted' }">
                  {{ stepSetupStatus.label }}
                </span>
              </div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">2</span>
                  <span class="step-text">Select a Mount Point</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepMountPointStatus.tone === 'success', 'bg-secondary': stepMountPointStatus.tone === 'muted' }">
                  {{ stepMountPointStatus.label }}
                </span>
              </div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">3</span>
                  <span class="step-text">Set Analysis Duration</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepDurationStatus.tone === 'success', 'bg-secondary': stepDurationStatus.tone === 'muted' }">
                  {{ stepDurationStatus.label }}
                </span>
              </div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">4</span>
                  <span class="step-text">Analyze Now → Refresh Result</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepAnalyzeStatus.tone === 'success', 'bg-info': stepAnalyzeStatus.tone === 'info', 'bg-secondary': stepAnalyzeStatus.tone === 'muted' }">
                  {{ stepAnalyzeStatus.label }}
                </span>
              </div>
            </div>

            <div class="mb-2">
              <div class="small mb-1">Select a Mount Point:</div>
              <div class="d-flex align-items-center">
                <div class="mp-select-wrapper" style="min-width: 280px;">
                  <select class="form-select form-select-sm"
                          [ngModel]="getConnectionKey(currentConnection)"
                          (ngModelChange)="selectFromDropdown($event)">
                    <option value=""></option>
                    <option *ngFor="let c of sortedConnections" [value]="getConnectionKey(c)">
                      {{ c.mountPoint }}
                    </option>
                  </select>
                </div>
                <button class="btn btn-outline-secondary btn-sm ms-2" type="button"
                        (click)="openMountpointConnectionSetup()"
                        matTooltip="Add or edit your mount point connection settings.">
                  <span class="d-inline-flex align-items-center">
                    <span class="material-icons me-1" style="font-size: 1.1rem;">settings</span>
                    <span>MountPoint Setup</span>
                  </span>
                </button>
              </div>
            </div>

            <div class="row g-2">
              <div class="col-md-5">
                <label class="form-label small d-inline-flex align-items-center">
                  <span>Analysis Duration</span>
                  <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                          matTooltip="How long to analyze RTCM data from the selected mount point.">
                    <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                  </button>
                </label>
                <select class="form-select form-select-sm"
                        [ngModel]="getAnalysisDurationSelection(conn)"
                        (ngModelChange)="onAnalysisDurationChange(conn, $event)">
                  <option *ngFor="let opt of durationPresetOptions" [ngValue]="opt.minutes">{{ opt.label }}</option>
                </select>
              </div>
            </div>

            <div class="d-flex align-items-center mt-3">
              <button class="btn btn-sm btn-primary"
                      (click)="onStartAnalysis(conn)"
                      [disabled]="analysisLoadingByMountpoint[conn.mountPoint || '']"
                      matTooltip="Starts an analyzer task in backend. You can leave this page and come back later.">
                <span class="d-inline-flex align-items-center">
                  <span class="material-icons me-1" style="font-size: 1.1rem;">troubleshoot</span>
                  <span>{{ hasAnalysisResult(conn) ? 'Re-analyze this RTK Base' : 'Analyze Now' }}</span>
                </span>
              </button>
              <button class="btn btn-sm btn-outline-primary ms-2"
                      type="button"
                      (click)="onRefreshAnalysisResult(conn)"
                      [disabled]="analysisFetchingByMountpoint[conn.mountPoint || '']">
                <span class="d-inline-flex align-items-center">
                  <span class="material-icons me-1" style="font-size: 1.1rem;" aria-hidden="true">refresh</span>
                  <span>Refresh</span>
                </span>
              </button>
            </div>
          </div>

          <!-- Satellite Signal Quality subsection removed -->

          <div class="row mt-3 subsection" *ngIf="showMap && hasAnalyzerSummary(conn.mountPoint || '')">
            <div class="col-12">
              <div class="d-flex justify-content-between align-items-center mb-2 subheader">
                <h4 class="m-0 subsection-title">RTK Base Station Location</h4>
                <div class="ms-auto d-flex align-items-center gap-3">
                  <button type="button" class="btn btn-sm btn-link text-decoration-none chevron-btn"
                          (click)="toggleMapSection(conn.mountPoint || '')"
                          [attr.aria-expanded]="!isMapCollapsed(conn.mountPoint || '')"
                          aria-label="Toggle map">
                    <span class="material-icons" *ngIf="isMapCollapsed(conn.mountPoint || '')">chevron_right</span>
                    <span class="material-icons" *ngIf="!isMapCollapsed(conn.mountPoint || '')">expand_more</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="col-12" *ngIf="!isMapCollapsed(conn.mountPoint || '') && analyzerSummaryByMountpoint[conn.mountPoint || ''] as mapSummary">
              <div class="mp-meta mb-2">
                <div>RTK Base Coordinate: <span class="mp-value">{{ mapSummary['location'] }}</span></div>
                <div>RTK base Geodetic Height (meter): <span class="mp-value">{{ mapSummary['geodetic_height_meters'] }}</span></div>
                <div>RTK base Orthometric Height (meter): <span class="mp-value">{{ mapSummary['orthometric_height_meters'] }}</span></div>
              </div>
            </div>
            <div class="col-12" *ngIf="!isMapCollapsed(conn.mountPoint || '')">
              <div class="map-box" [id]="getMapElementId(conn.mountPoint || '')"></div>
            </div>
            <div class="col-12" *ngIf="!isMapCollapsed(conn.mountPoint || '')">
              <div class="ext-map-grid w-100" style="grid-template-columns: repeat(3, 1fr);">
                <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getGoogleMapUrl(conn.mountPoint || '', 'sat')">Google Satellite</a>
                <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getGoogleMapUrl(conn.mountPoint || '', 'map')">Google Street</a>
                <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBingMapUrl(conn.mountPoint || '', 'sat')">Bing Satellite</a>
                <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBingMapUrl(conn.mountPoint || '', 'map')">Bing Street</a>
                <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getAmapUrl(conn.mountPoint || '', 'map')">Amap Street & Satellite</a>
                <a class="btn btn-sm btn-outline-secondary w-100" target="_blank" rel="noopener" [href]="getBaiduUrl(conn.mountPoint || '', 'map')">Baidu Street & Satellite</a>
              </div>
            </div>
          </div>

          <div class="analysis-result mt-3">
            <div class="file-card">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="subsection-title m-0 d-inline-flex align-items-center">
                  <span>Analysis Result</span>
                  <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                          matTooltip="Use Refresh above to load the latest report. Use Expand/Collapse to browse JSON.">
                    <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                  </button>
                </div>
              </div>

              <div class="d-flex gap-2 mb-2" *ngIf="hasAnalysisResult(conn)">
                <button class="btn btn-sm btn-outline-secondary" (click)="expandAll(conn.mountPoint || '')">Expand All</button>
                <button class="btn btn-sm btn-outline-secondary" (click)="collapseAll(conn.mountPoint || '')">Collapse All</button>
                <button class="btn btn-sm btn-outline-secondary" (click)="copyJson(conn)">Copy JSON</button>
              </div>

              <ng-container *ngIf="analysisResultsByMountpoint[conn.mountPoint || ''] as json; else noAnalysisResult">
                <div class="json-tree">
                  <ng-template #renderNode let-key="key" let-value="value" let-path="path" let-mp="mp">
                    <div class="json-row" *ngIf="!isObject(value) && !isArray(value)">
                      <span class="json-key">{{ key }}:</span>
                      <a *ngIf="isUrl(value)" class="json-value" [href]="value" target="_blank" rel="noopener">{{ value }}</a>
                      <span *ngIf="!isUrl(value)" class="json-value">{{ value }}</span>
                    </div>
                    <div class="json-node" *ngIf="isObject(value)">
                      <div class="json-key clickable" (click)="toggleNode(mp, path)">
                        {{ isExpanded(mp, path) ? '▼' : '▶' }} {{ key }}:
                      </div>
                      <div class="json-children" *ngIf="isExpanded(mp, path)">
                        <ng-container *ngFor="let child of value | keyvalue:keyOrder">
                          <ng-container *ngTemplateOutlet="renderNode; context: { key: child.key, value: child.value, path: path + '.' + child.key, mp: mp }"></ng-container>
                        </ng-container>
                      </div>
                    </div>
                    <div class="json-array" *ngIf="isArray(value)">
                      <div class="json-key clickable" (click)="toggleNode(mp, path)">
                        {{ isExpanded(mp, path) ? '▼' : '▶' }} {{ key }}:
                      </div>
                      <ul class="json-ul" *ngIf="isExpanded(mp, path)">
                        <li *ngFor="let v of value; let idx = index">
                          <ng-container *ngTemplateOutlet="renderNode; context: { key: idx, value: v, path: path + '[' + idx + ']', mp: mp }"></ng-container>
                        </li>
                      </ul>
                    </div>
                  </ng-template>

                  <ng-container *ngIf="isObject(json); else renderRootArray">
                    <ng-container *ngFor="let item of json | keyvalue:keyOrder">
                      <ng-container *ngTemplateOutlet="renderNode; context: { key: item.key, value: item.value, path: item.key, mp: conn.mountPoint || '' }"></ng-container>
                    </ng-container>
                  </ng-container>
                  <ng-template #renderRootArray>
                    <div class="json-array">
                      <ul class="json-ul">
                        <li *ngFor="let v of json; let idx = index">
                          <ng-container *ngTemplateOutlet="renderNode; context: { key: idx, value: v, path: '[' + idx + ']', mp: conn.mountPoint || '' }"></ng-container>
                        </li>
                      </ul>
                    </div>
                  </ng-template>
                </div>
              </ng-container>

              <ng-template #noAnalysisResult>
                <div class="text-muted small">
                  No analysis report available yet. Click Analyze Now, then use Refresh to check status and load the report. Please make sure the mountpoint you are analyzing is up and running.
                </div>
              </ng-template>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-5 d-flex align-items-start">
          <img src="assets/images/landing/GeoAstra_RTK_Base_Analyzer_diagram.drawio.png"
               alt="RTK Base Analyzer Diagram"
               class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
        </div>
      </div>
    </div>
  </ng-container>

  <ng-template #emptyState>
    <div class="mp-card">
      <div class="subsection">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <div class="subsection-title">No mount point connections yet</div>
            <div class="text-muted small mt-1">
              Click <strong>MountPoint Setup</strong> to add your first mount point connection.
            </div>
          </div>
          <button class="btn btn-primary btn-sm" type="button" (click)="openMountpointConnectionSetup()">
            <span class="d-inline-flex align-items-center">
              <span class="material-icons me-1" style="font-size: 1.1rem;">settings</span>
              <span>MountPoint Setup</span>
            </span>
          </button>
        </div>
        <div class="text-muted small mt-2" *ngIf="isLoading">
          Loading connections...
        </div>
        <div class="text-danger small mt-2" *ngIf="!isLoading && errorMessage">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </ng-template>
</div>


